import { RequirementType } from "@wingspanhq/payments/dist/interfaces";

import { useMutationCreateBackgroundCheckSettings } from "../../../query/backgroundChecks/mutations/useMutationCreateBackgroundCheckSettings";
import { useMutationCreateMonitorSettings } from "../../../query/monitorSettings/mutations/useMutationCreateMonitorSettings";
import { useMutationCreateSharedFileSettings } from "../../../query/sharedFile/mutations/useMutationCreateSharedFileSettings";
import { cleanOptionalFormValues } from "../../../shared/utils/cleanOptionalFormValues";
import { RequirementDefinitionTemplate } from "../routes/RouteCreateRequirementByTemplate/types";
import {
  BaseDataSourceResponse,
  RequirementDefinitionFormValues
} from "../types";
import { baseMapFormDataToRequirementCreateRequest } from "../utils/baseMapFormDataToRequirementCreateRequest";

export const useCreateRequirementDefinitionSettings = () => {
  const [createSharedFileSettings, createSharedFileSettingsMeta] =
    useMutationCreateSharedFileSettings();
  const [createMonitorSettings, createMonitorSettingsMeta] =
    useMutationCreateMonitorSettings();
  const [createBackgroundCheckSettings, createBackgroundCheckSettingsMeta] =
    useMutationCreateBackgroundCheckSettings();

  const submit = async ({
    template,
    formData
  }: {
    formData: RequirementDefinitionFormValues;
    template: RequirementDefinitionTemplate;
  }) => {
    const formValues = cleanOptionalFormValues(formData, {
      ignore: ["coverages"]
    });

    const {
      mapFormDataToDataSourceCreateRequest,
      mapDataSourceResponseToRequirementCreateRequest,
      mapFormDataToRequirementCreateRequest
    } = template.form || {};

    const { requirementType } = template;

    const createDataSourceRequest = mapFormDataToDataSourceCreateRequest?.(
      formValues,
      template
    );

    const createSourceData =
      requirementType === RequirementType.SharedFile
        ? createSharedFileSettings
        : requirementType === RequirementType.InsuranceCoverage
          ? createMonitorSettings
          : requirementType === RequirementType.BackgroundCheck
            ? createBackgroundCheckSettings
            : null;

    return new Promise<
      Partial<BaseDataSourceResponse> &
        ReturnType<typeof baseMapFormDataToRequirementCreateRequest>
    >((resolve, reject) => {
      if (createSourceData && mapDataSourceResponseToRequirementCreateRequest) {
        createSourceData?.(createDataSourceRequest, {
          onError(error: Error) {
            reject(error);
          },
          onSuccess(response) {
            resolve({
              ...mapDataSourceResponseToRequirementCreateRequest(
                response,
                template
              ),
              ...baseMapFormDataToRequirementCreateRequest(formValues)
            });
          }
        });
      } else {
        resolve({
          ...mapFormDataToRequirementCreateRequest?.(formValues, template),
          ...baseMapFormDataToRequirementCreateRequest(formValues)
        });
      }
    });
  };

  return {
    errors:
      createSharedFileSettingsMeta.error ||
      createMonitorSettingsMeta.error ||
      createBackgroundCheckSettingsMeta.error,
    isLoading:
      createSharedFileSettingsMeta.isLoading ||
      createMonitorSettingsMeta.isLoading ||
      createBackgroundCheckSettingsMeta.isLoading,
    submit
  };
};
